/**
 * Single source of truth for "what string do we render in the UI for
 * this device?" — cards, sidebars, toolbar headers, and *especially*
 * toasts/notifications must agree, otherwise the user sees `RMX3710`
 * in the device list and a raw `FERSJ7EYMBMJLNRW` in the
 * failover banner for the same phone.
 *
 * Priority chain (most → least preferred):
 *   1. `alias`         (user-set nickname, e.g. "QA Pixel #2")
 *   2. `meta.model`    (persisted `ro.product.model`, stable across
 *                       USB ↔ Wi-Fi transports)
 *   3. `model`         (live `adb devices -l` model — only present
 *                       when adb is currently serving the device)
 *   4. `product`       (live `adb devices -l` product — same caveat)
 *   5. `serial`        (last-resort fallback so the UI never renders
 *                       an empty string)
 *
 * Why a function not a hook: same lookup logic is needed inside
 * Zustand selectors and toast factories where hooks aren't allowed.
 * The hook wrapper is just `useDeviceDisplayName(serial)` for React
 * call sites that already hold a serial.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { devicesApi } from '@/lib/api';
import type { DeviceInfo, DeviceMeta, DeviceRecord } from '@/lib/types';

/** Subset of fields formatDisplayName actually reads. */
export interface DeviceLike {
  serial?: string;
  alias?: string;
  meta?: DeviceMeta;
  model?: string;
  product?: string;
}

/**
 * Compute the canonical display string for a device-shaped object.
 * `serial` falls back to the empty string when omitted — callers
 * with no serial in hand should pass it as the final argument so we
 * never return `undefined`.
 */
export function formatDisplayName(d: DeviceLike | null | undefined, fallbackSerial?: string): string {
  if (!d) return fallbackSerial ?? '';
  return (
    d.alias ||
    d.meta?.model ||
    d.model ||
    d.product ||
    d.serial ||
    fallbackSerial ||
    ''
  );
}

type AnyDevice = DeviceRecord & Partial<DeviceInfo>;

/**
 * React hook variant: given a serial, returns the formatted display
 * string from the shared `['devices']` query cache. Falls back to the
 * serial itself when the cache hasn't loaded yet OR when the serial
 * isn't in the cache (e.g. an unregistered transport).
 *
 * Cache-friendly: the underlying `useQuery` is the same one the
 * devices grid + sidebar already hold open, so this is a free cache
 * read in the steady state.
 */
export function useDeviceDisplayName(serial: string | null | undefined): string {
  const { data } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
    // `staleTime: Infinity`-ish — we piggyback the grid's refetch
    // cadence so the toast doesn't trigger its own polling. The grid
    // already polls every 5 s.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    if (!serial) return '';
    const devices = (data?.devices ?? []) as AnyDevice[];
    const hit = devices.find(
      (d) => d.serial === serial || d.transports?.some((t) => t.serial === serial)
    );
    return formatDisplayName(hit, serial);
  }, [serial, data]);
}
